/**
 * DQRuleDetailsModal — v3
 *
 * Added:
 *   • PolicyContextPanel  — shows the full policy_context sub-object with
 *     verbatim excerpt, metadata grid, confidence bar, and allowed values.
 *   • "Policy Driven" header badge when policy_context is present.
 *
 * Unchanged: all existing sections (Execution Summary, Rule Definition,
 * Logic, Merit, Sample Failures, footer actions).
 *
 * Uses T tokens and CSS classes from theme.ts throughout.
 */

import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle, faTimesCircle, faPen, faWandMagicSparkles,
  faExclamationTriangle, faTag, faCode, faFileLines,
  faChevronDown, faChevronUp, faQuoteLeft,
  faShieldHalved, faCircleCheck, faBookOpen, faChartBar, faListAlt,
} from '@fortawesome/free-solid-svg-icons';
import { T, injectTheme } from '../theme/theme';
import TokenMapEditor from '../dq/TokenMapEditor';


// ─── Types ────────────────────────────────────────────────────────────────────
type PolicyContext = {
  file_name:          string;
  page:               string;
  section:            string;
  raw_excerpt:        string;
  business_statement: string;
  business_intent:    string;
  business_risk:      string;
  allowed_values:     string[];
  rule_type:          string;
  severity:           string;
  confidence:         number;
};

type Props = {
  rule:                      any | null;
  show:                      boolean;
  onClose:                   () => void;
  onDQRuleUpdateLifeCycle?:  (rule: any, lifecycle: string) => void;
  onDQRuleTuningTrigger?:    () => void;
  entityId?:                  string;
  sourcePrefix?:              string;
  column?:                    string;
  onTokenBlockSaved?:         (block: any) => void;
};

// ─── Constants ────────────────────────────────────────────────────────────────
const SEV_META: Record<string, { color: string; bg: string; label: string }> = {
  H: { color: T.red,   bg: T.redBg,   label: 'High' },
  M: { color: T.amber, bg: T.amberBg, label: 'Medium' },
  L: { color: T.green, bg: T.greenBg, label: 'Low' },
};

const LC_META: Record<string, { color: string; bg: string; border: string }> = {
  Approved: { color: T.green,     bg: T.greenBg,   border: T.greenBorder },
  Draft:    { color: T.textMuted, bg: T.bgSidebar,  border: T.border },
  Rejected: { color: T.red,       bg: T.redBg,      border: T.redBorder },
  Waiting:  { color: T.amber,     bg: T.amberBg,    border: T.amberBorder },
};

const RULE_TYPE_LABELS: Record<string, string> = {
  mandatory:      'Mandatory',
  allowed_values: 'Allowed Values',
  cross_field:    'Cross-field',
  format:         'Format',
  range:          'Range',
  temporal:       'Temporal',
  uniqueness:     'Uniqueness',
  lookup:         'Lookup',
  free_text:      'Free-text',
};

// ─── Shared layout atoms ──────────────────────────────────────────────────────
function Section({
  title, icon, children, accent,
}: {
  title: string; icon: any; children: React.ReactNode; accent?: string;
}) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
        paddingBottom: 8, borderBottom: `1px solid ${T.border}`,
      }}>
        <FontAwesomeIcon icon={icon} style={{ fontSize: 13, color: accent || T.brandMid }} />
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9, fontWeight: 700, textTransform: 'uppercase' as const,
          letterSpacing: '.12em', color: accent || T.textMuted,
        }}>
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

function DefRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', gap: 12, padding: '8px 0',
      borderBottom: `1px solid ${T.border}`,
    }}>
      <div style={{ minWidth: 148, fontSize: 12, fontWeight: 700, color: T.textMuted }}>{label}</div>
      <div style={{ fontSize: 13, color: T.textPrimary, flex: 1 }}>{children}</div>
    </div>
  );
}

function ConfidenceBar({ value }: { value: number }) {
  const pct  = Math.round(value * 100);
  const color = pct >= 80 ? T.green : pct >= 60 ? T.amber : T.red;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{
        flex: 1, height: 6, background: T.bgSidebar,
        borderRadius: 3, overflow: 'hidden', border: `1px solid ${T.border}`,
      }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: color, borderRadius: 3,
          transition: 'width .4s ease',
        }} />
      </div>
      <span style={{
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 12, fontWeight: 700, color, minWidth: 36,
      }}>
        {pct}%
      </span>
    </div>
  );
}

// ─── Policy Context Panel ─────────────────────────────────────────────────────
function PolicyContextPanel({ ctx }: { ctx: PolicyContext }) {
  const [excerptOpen, setExcerptOpen] = useState(true);

  const rtLabel = RULE_TYPE_LABELS[ctx.rule_type] || ctx.rule_type;
  const sevColor = ctx.severity === 'mandatory' ? T.red : T.amber;
  const sevBg    = ctx.severity === 'mandatory' ? T.redBg : T.amberBg;
  const sevBd    = ctx.severity === 'mandatory' ? T.redBorder : T.amberBorder;
  const sevLabel = ctx.severity === 'mandatory' ? 'Mandatory' : 'Warning';

  return (
    <Section title="Policy Provenance" icon={faShieldHalved} accent={T.brand}>

      {/* ── Document locator card ── */}
      <div style={{
        background: T.brandLight,
        border: `1px solid ${T.brandMid}`,
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 14,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 8, flexShrink: 0,
          background: T.brand, display: 'flex', alignItems: 'center',
          justifyContent: 'center',
        }}>
          <FontAwesomeIcon icon={faFileLines} style={{ fontSize: 16, color: '#fff' }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13, fontWeight: 700, color: T.brand,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const,
          }}>
            {ctx.file_name}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' as const }}>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, fontWeight: 700, color: T.brand,
              background: 'rgba(30,58,110,.12)', borderRadius: 6, padding: '1px 7px',
            }}>
              Page {ctx.page}
            </span>
            {ctx.section && (
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10, fontWeight: 700, color: T.textMuted,
                background: T.bgCard, border: `1px solid ${T.border}`,
                borderRadius: 6, padding: '1px 7px',
              }}>
                {ctx.section}
              </span>
            )}
          </div>
        </div>
        {/* Rule-type + severity chips */}
        <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 5, alignItems: 'flex-end' }}>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
            background: T.purpleBg, color: T.purple, border: `1px solid ${T.purpleBorder}`,
          }}>
            {rtLabel}
          </span>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
            background: sevBg, color: sevColor, border: `1px solid ${sevBd}`,
          }}>
            {sevLabel}
          </span>
        </div>
      </div>

      {/* ── Extracted constraint details ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 0 }}>
        {ctx.business_statement && (
          <div style={{ padding: '9px 0', borderBottom: `1px solid ${T.border}` }}>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 700, letterSpacing: '.1em',
              textTransform: 'uppercase' as const, color: T.textMuted, marginBottom: 5,
            }}>
              Extracted Constraint
            </div>
            <div style={{ fontSize: 13, color: T.textPrimary, fontWeight: 600, lineHeight: 1.5 }}>
              {ctx.business_statement}
            </div>
          </div>
        )}

        {ctx.business_intent && (
          <div style={{ padding: '9px 0', borderBottom: `1px solid ${T.border}` }}>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 700, letterSpacing: '.1em',
              textTransform: 'uppercase' as const, color: T.textMuted, marginBottom: 5,
            }}>
              Business Intent
            </div>
            <div style={{ fontSize: 12, color: T.textSecond, lineHeight: 1.6 }}>
              {ctx.business_intent}
            </div>
          </div>
        )}

        {ctx.business_risk && (
          <div style={{ padding: '9px 0', borderBottom: `1px solid ${T.border}` }}>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 700, letterSpacing: '.1em',
              textTransform: 'uppercase' as const, color: T.red, marginBottom: 5,
            }}>
              Risk If Violated
            </div>
            <div style={{
              fontSize: 12, color: T.red, lineHeight: 1.6,
              background: T.redBg, borderRadius: 6, padding: '6px 10px',
              border: `1px solid ${T.redBorder}`,
            }}>
              <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: 6, fontSize: 11 }} />
              {ctx.business_risk}
            </div>
          </div>
        )}

        {ctx.allowed_values?.length > 0 && (
          <div style={{ padding: '9px 0', borderBottom: `1px solid ${T.border}` }}>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 700, letterSpacing: '.1em',
              textTransform: 'uppercase' as const, color: T.textMuted, marginBottom: 5,
            }}>
              Allowed Values
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 5 }}>
              {ctx.allowed_values.map((v, i) => (
                <span key={i} style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11, fontWeight: 700,
                  background: T.greenBg, color: T.green,
                  border: `1px solid ${T.greenBorder}`,
                  borderRadius: 6, padding: '2px 10px',
                }}>
                  {v}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Extraction confidence */}
        <div style={{ padding: '9px 0', borderBottom: `1px solid ${T.border}` }}>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, fontWeight: 700, letterSpacing: '.1em',
            textTransform: 'uppercase' as const, color: T.textMuted, marginBottom: 8,
          }}>
            Extraction Confidence
          </div>
          <ConfidenceBar value={ctx.confidence} />
        </div>
      </div>

      {/* ── Verbatim excerpt ── collapsible ── */}
      {ctx.raw_excerpt && (
        <div style={{ marginTop: 12 }}>
          <button
            onClick={() => setExcerptOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 7,
              background: 'none', border: 'none', cursor: 'pointer', padding: 0,
              marginBottom: excerptOpen ? 8 : 0,
            }}
          >
            <FontAwesomeIcon icon={faQuoteLeft} style={{ fontSize: 11, color: T.brandMid }} />
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 700, letterSpacing: '.1em',
              textTransform: 'uppercase' as const, color: T.textMuted,
            }}>
              Verbatim Policy Text
            </span>
            <FontAwesomeIcon
              icon={excerptOpen ? faChevronUp : faChevronDown}
              style={{ fontSize: 9, color: T.textMuted, marginLeft: 4 }}
            />
          </button>

          {excerptOpen && (
            <div style={{
              borderLeft: `3px solid ${T.brandMid}`,
              paddingLeft: 14,
              marginLeft: 2,
            }}>
              <blockquote style={{
                margin: 0,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                lineHeight: 1.8,
                color: T.textMuted,
                fontStyle: 'italic',
                background: T.bgSidebar,
                borderRadius: '0 8px 8px 0',
                padding: '10px 14px',
                border: `1px solid ${T.border}`,
                borderLeft: 'none',
                maxHeight: 180,
                overflowY: 'auto' as const,
              }}>
                {ctx.raw_excerpt}
              </blockquote>
            </div>
          )}
        </div>
      )}
    </Section>
  );
}


// ─── CollapsibleSection — looks like old Section, but toggleable ──────────────
function CollapsibleSection({
  title, icon, accent, children, defaultOpen = true, badge,
}: {
  title: string;
  icon: any;
  accent?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  badge?: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div style={{ marginBottom: 20 }}>
      {/* Header row — same look as old Section, now clickable */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '0 0 8px 0', marginBottom: open ? 12 : 0,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FontAwesomeIcon
            icon={icon}
            style={{ fontSize: 13, color: accent || T.brandMid }}
          />
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, fontWeight: 700, textTransform: 'uppercase' as const,
            letterSpacing: '.12em', color: accent || T.textMuted,
          }}>
            {title}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {badge}
          <FontAwesomeIcon
            icon={open ? faChevronUp : faChevronDown}
            style={{ fontSize: 9, color: T.textMuted }}
          />
        </div>
      </button>

      {open && <div>{children}</div>}
    </div>
  );
}

// ─── Examples Section ─────────────────────────────────────────────────────────
const ExamplesSection: React.FC<{ rule: any }> = ({ rule }) => {
  const examples: any[] = rule.ex ?? rule.examples ?? [];
  if (!examples.length) return null;

  return (
    <CollapsibleSection
      title="Examples"
      icon={faListAlt}
      defaultOpen={true}
      badge={
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9, fontWeight: 700,
          color: T.textMuted,
        }}>
          {examples.length} shown
        </span>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 7 }}>
        {examples.map((ex: any, i: number) => (
          <div key={i} style={{
            background: T.bgSidebar,
            border: `1px solid ${T.border}`,
            borderRadius: 8, padding: '9px 11px',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center',
              gap: 8, flexWrap: 'wrap' as const,
            }}>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12, color: T.red,
                background: T.redBg, border: `1px solid ${T.redBorder}`,
                borderRadius: 5, padding: '2px 7px',
              }}>
                {ex.before}
              </span>
              <span style={{ color: T.textMuted, fontSize: 12 }}>→</span>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 12, color: T.green,
                background: T.greenBg, border: `1px solid ${T.greenBorder}`,
                borderRadius: 5, padding: '2px 7px',
              }}>
                {ex.after}
              </span>
            </div>
            {ex.note && (
              <div style={{
                fontSize: 11, color: T.textMuted,
                marginTop: 5, fontStyle: 'italic',
              }}>
                {ex.note}
              </div>
            )}
          </div>
        ))}
      </div>
    </CollapsibleSection>
  );
};

// ─── Logic Section — Transform or Validation expression ──────────────────────
const LogicSection: React.FC<{ rule: any; isStd: boolean }> = ({ rule, isStd }) => {
  const hasExpr = isStd ? !!rule.te : !!(rule.vl || rule.ve || rule.vc);
  if (!hasExpr) return null;

  const exprValue = isStd
    ? JSON.stringify(rule.te, null, 2)
    : JSON.stringify(rule.ve, null, 2) || rule.vl || rule.vc;

  return (
    <CollapsibleSection
      title={isStd ? 'Transform Expression' : 'Validation Expression'}
      icon={faCode}
      defaultOpen={false}
    >
      <pre style={{
        background: T.bgSidebar, border: `1px solid ${T.border}`,
        borderRadius: 8, padding: 12,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: 11, color: T.textPrimary,
        overflowX: 'auto' as const, margin: 0,
        whiteSpace: 'pre-wrap' as const,
      }}>
        {exprValue}
      </pre>
    </CollapsibleSection>
  );
};

// ─── Token Map Section — only for std rules with te + entityId context ────────
const TokenMapSection: React.FC<{
  rule: any;
  isStd: boolean;
  entityId?: string;
  sourcePrefix?: string;
  column?: string;
  onTokenBlockSaved?: (block: any) => void;
}> = ({ rule, isStd, entityId, sourcePrefix, column, onTokenBlockSaved }) => {
  if (!isStd || !rule.te || !entityId || !sourcePrefix || !column) return null;

  return (
    <CollapsibleSection
      title="Token Map"
      icon={faBookOpen}
      defaultOpen={false}
      badge={
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
          background: T.purpleBg, color: T.purple, border: `1px solid ${T.purpleBorder}`,
        }}>
          Editable
        </span>
      }
    >
      <TokenMapEditor
        entityId={entityId}
        sourcePrefix={sourcePrefix}
        column={column}
        ruleId={rule.id}
        onSaved={(block: any) => {
          if (onTokenBlockSaved) onTokenBlockSaved(block);
        }}
      />
    </CollapsibleSection>
  );
};

// ─── Impact Section — Applied (std) or Failures (val) ────────────────────────
const ImpactSection: React.FC<{ rule: any; isStd: boolean }> = ({ rule, isStd }) => {
  const result   = rule.result ?? {};
  const total    = result.total_records ?? 0;

  // ── STD: show applied records from result.examples ──
  if (isStd) {
    const resultEx = result.examples;
    if (!resultEx) return null;

    const affected = resultEx.affected_count ?? 0;
    const pct      = total > 0 ? +((affected / total) * 100).toFixed(1) : 0;

    const rows: Array<{ row: number; before: string; after: string }> =
      (resultEx.sample_changes ?? []).flatMap((change: any) =>
        Object.values(change.field_changes ?? {}).map((vals: any) => ({
          row:    change.index,
          before: vals.before,
          after:  vals.after,
        }))
      );

    return (
      <CollapsibleSection
        title="Impact"
        icon={faChartBar}
        defaultOpen={false}
        badge={
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
            background: '#E6F1FB', color: '#0C447C', border: '1px solid #B5D4F4',
          }}>
            {pct}% applied
          </span>
        }
      >
        {/* Count + bar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
          <span style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
            background: '#E6F1FB', color: '#0C447C', border: '1px solid #B5D4F4',
          }}>
            APPLIED
          </span>
          <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>
            {affected.toLocaleString()} applied
          </span>
          <span style={{ fontSize: 12, color: T.textMuted }}>
            / {total.toLocaleString()} total
          </span>
        </div>
        <div style={{
          height: 4, background: T.bgSidebar, border: `1px solid ${T.border}`,
          borderRadius: 2, overflow: 'hidden', marginBottom: 12,
        }}>
          <div style={{
            width: `${pct}%`, height: '100%',
            background: '#185FA5', borderRadius: 2,
          }} />
        </div>

        {/* Sample rows */}
        {rows.length > 0 ? rows.map((item, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0',
            borderBottom: i < rows.length - 1 ? `1px solid ${T.border}` : 'none',
          }}>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10, color: T.textMuted, minWidth: 28,
            }}>
              #{item.row}
            </span>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, color: T.red, flex: 1,
            }}>
              {item.before}
            </span>
            <span style={{ color: T.textMuted, fontSize: 12 }}>→</span>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12, color: T.green, flex: 1,
            }}>
              {item.after}
            </span>
          </div>
        )) : (
          <div style={{ fontSize: 12, color: T.textMuted, fontStyle: 'italic' }}>
            No sample changes available.
          </div>
        )}
        <div style={{ marginTop: 8, fontSize: 11, color: T.textMuted }}>
          Showing {rows.length} of {affected.toLocaleString()} affected records
        </div>
      </CollapsibleSection>
    );
  }

  // ── VAL: show failing records ──
  const failCount  = result.fail_count ?? 0;
  const failures   = result.sample_failures ?? [];
  if (!total) return null;

  const failPct = +((failCount / total) * 100).toFixed(1);

  return (
    <CollapsibleSection
      title="Failures"
      icon={faExclamationTriangle}
      accent={failCount > 0 ? T.red : T.green}
      defaultOpen={false}
      badge={
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
          background: failCount > 0 ? T.redBg   : T.greenBg,
          color:      failCount > 0 ? '#791F1F'  : '#27500A',
          border: `1px solid ${failCount > 0 ? T.redBorder : T.greenBorder}`,
        }}>
          {failPct}% fail rate
        </span>
      }
    >
      {/* Count + bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
          background: failCount > 0 ? T.redBg  : T.greenBg,
          color:      failCount > 0 ? '#791F1F' : '#27500A',
          border: `1px solid ${failCount > 0 ? T.redBorder : T.greenBorder}`,
        }}>
          {failCount > 0 ? 'FAILING' : 'PASSING'}
        </span>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary }}>
          {failCount.toLocaleString()} failing
        </span>
        <span style={{ fontSize: 12, color: T.textMuted }}>
          / {total.toLocaleString()} total
        </span>
      </div>
      <div style={{
        height: 4, background: T.bgSidebar, border: `1px solid ${T.border}`,
        borderRadius: 2, overflow: 'hidden', marginBottom: 12,
      }}>
        <div style={{
          width: `${failPct}%`, height: '100%',
          background: failCount > 0 ? T.red : T.green,
          borderRadius: 2,
        }} />
      </div>

      {/* Sample failure messages */}
      {failures.length > 0 ? (
        <div>
          {failures.map((f: any, i: number) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, padding: '6px 0',
              borderBottom: i < failures.length - 1 ? `1px solid ${T.border}` : 'none',
            }}>
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                style={{ fontSize: 11, color: T.red, marginTop: 2, flexShrink: 0 }}
              />
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11, color: T.textPrimary, lineHeight: 1.5,
              }}>
                {f.error ?? f.failure_reason ?? f.message ?? JSON.stringify(f)}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div style={{
          fontSize: 12, color: T.green, fontStyle: 'italic',
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 12 }} />
          No failures detected across {total.toLocaleString()} records.
        </div>
      )}
    </CollapsibleSection>
  );
};

// ─── Main modal ───────────────────────────────────────────────────────────────
const RuleDetailsModal: React.FC<Props> = ({
  rule, show, onClose,
  onDQRuleUpdateLifeCycle,
  onDQRuleTuningTrigger,
  entityId,
  sourcePrefix,
  column,
  onTokenBlockSaved
}) => {
  // Inject shared theme CSS on first render
  React.useEffect(() => { injectTheme(); }, []);

  if (!rule) return null;

  const isStd      = rule.cat === 'std';
  const result     = rule.result || {};
  const status     = result.status || 'N/A';
  const lifecycle  = rule.lifecycle || 'Draft';
  const lcMeta     = LC_META[lifecycle] || LC_META.Draft;
  const sev        = rule.sev || 'L';
  const sevMeta    = SEV_META[sev] || SEV_META.L;
  const policyCtx  = rule.policy_context as PolicyContext | null;

  const statusIsPass = status === 'PASS';
  const statusIsFail = status === 'FAIL' || status === 'ERROR';
  const totalRecords = result.total_records;
  const failCount    = result.fail_count || 0;
  const failPct      = totalRecords ? ((failCount / totalRecords) * 100).toFixed(1) : '—';

  return (
    <Modal show={show} onHide={onClose} size="xl" centered
      contentClassName="border-0 shadow-lg"
      style={{ fontFamily: "'Sora', system-ui, sans-serif" }}>

      {/* ── Header ── */}
      <div style={{
        background: T.brand, padding: '18px 24px 16px',
        borderRadius: '12px 12px 0 0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 700, textTransform: 'uppercase' as const,
              letterSpacing: '.1em', color: 'rgba(255,255,255,.5)',
            }}>
              Rule Details
            </span>
            {/* Policy-driven badge */}
            {policyCtx && (
              <span style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9, fontWeight: 700,
                background: 'rgba(255,255,255,.18)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,.35)',
                borderRadius: 8, padding: '2px 8px',
                letterSpacing: '.07em',
              }}>
                <FontAwesomeIcon icon={faCircleCheck} style={{ marginRight: 5, fontSize: 9 }} />
                POLICY DRIVEN
              </span>
            )}
          </div>
          <div style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 20, fontWeight: 700, color: '#fff', letterSpacing: '-.01em',
          }}>
            {rule.id}
          </div>
          {rule.d && (
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,.7)', marginTop: 3, maxWidth: 500 }}>
              {rule.d}
            </div>
          )}
        </div>
        <button onClick={onClose} style={{
          background: 'rgba(255,255,255,.15)', border: 'none',
          color: '#fff', borderRadius: 8, width: 32, height: 32,
          cursor: 'pointer', fontSize: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>×</button>
      </div>

      {/* ── Status bar ── */}
      <div style={{
        display: 'flex', background: T.bgSidebar,
        borderBottom: `1px solid ${T.border}`,
      }}>
        {[
          { label: 'Status',    value: status,          color: statusIsPass ? T.green : statusIsFail ? T.red : T.textMuted },
          { label: 'Lifecycle', value: lifecycle,        color: lcMeta.color },
          { label: 'Severity',  value: sevMeta.label,   color: sevMeta.color },
          { label: 'Category',  value: (rule.cat || '').toUpperCase(), color: T.purple },
          { label: 'Dimension', value: rule.dq_dim || '—', color: T.brand },
        ].map(({ label, value, color }, i) => (
          <div key={i} style={{
            flex: 1, padding: '10px 16px',
            borderRight: i < 4 ? `1px solid ${T.border}` : 'none',
          }}>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9, fontWeight: 700, textTransform: 'uppercase' as const,
              letterSpacing: '.08em', color: T.textMuted, marginBottom: 3,
            }}>
              {label}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Body ── */}

      {/* ── Body ── */}
      <div style={{ overflowY: 'auto' as const, maxHeight: '62vh', background: T.bgCard, padding: '20px 24px' }}>

        {/* Execution summary — still uses CollapsibleSection, open by default */}
        <CollapsibleSection title="Execution Summary" icon={faCheckCircle} defaultOpen={true}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {[
              {
                label: 'Total Records',
                value: result.total_records?.toLocaleString() ?? '—',
                highlight: false,
              },
              {
                label: isStd ? 'Applied Records' : 'Failing Records',
                value: isStd
                  ? (result.examples?.affected_count?.toLocaleString() ?? '0')
                  : (result.fail_count ?? 0).toLocaleString(),
                highlight: !isStd && (result.fail_count ?? 0) > 0,
              },
              {
                label: isStd ? 'Pass Rate' : 'Fail Rate',
                value: result.total_records
                  ? isStd
                    ? '100%'   // std rules that ran are always APPLIED = pass
                    : `${((result.fail_count / result.total_records) * 100).toFixed(1)}%`
                  : '—',
                highlight: !isStd && (result.fail_count ?? 0) > 0,
              },
            ].map(({ label, value, highlight }, i) => (
              <div key={i} style={{
                background: highlight ? T.redBg : T.bgSidebar,
                border: `1px solid ${highlight ? T.redBorder : T.border}`,
                borderRadius: 10, padding: '10px 14px',
              }}>
                <div style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 9, fontWeight: 700, textTransform: 'uppercase' as const,
                  letterSpacing: '.08em',
                  color: highlight ? T.red : T.textMuted, marginBottom: 4,
                }}>
                  {label}
                </div>
                <div style={{
                  fontSize: 18, fontWeight: 700,
                  color: highlight ? T.red : T.textPrimary,
                }}>
                  {value}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleSection>

        {policyCtx && <PolicyContextPanel ctx={policyCtx} />}

        {/* Rule definition */}
        <CollapsibleSection title="Rule Definition" icon={faTag} defaultOpen={true}>
          <DefRow label="Target Field">
            {Array.isArray(rule.field) ? rule.field.join(', ') : rule.field || '—'}
          </DefRow>
          <DefRow label="Severity">
            <span style={{
              fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 10,
              background: sevMeta.bg, color: sevMeta.color,
            }}>
              {sevMeta.label}
            </span>
          </DefRow>
          {rule.bi  && <DefRow label="Business Impact">{rule.bi}</DefRow>}
          {rule.act && (
            <DefRow label="Action on Failure">
              <span style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700,
                background: T.bgSidebar, border: `1px solid ${T.border}`,
                borderRadius: 6, padding: '2px 8px',
              }}>
                {rule.act}
              </span>
            </DefRow>
          )}
          {rule.fix && (
            <DefRow label="Remediation">
              <span style={{ color: T.green }}>{rule.fix}</span>
            </DefRow>
          )}
          {rule.policy_ref && (
            <DefRow label="Policy Reference">
              <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: T.brand }}>
                {rule.policy_ref}
              </span>
            </DefRow>
          )}
          {rule.n && <DefRow label="Notes"><em style={{ color: T.textMuted }}>{rule.n}</em></DefRow>}
        </CollapsibleSection>

        <ExamplesSection rule={rule} />
        <ImpactSection rule={rule} isStd={isStd} />
        <LogicSection rule={rule} isStd={isStd} />
        <TokenMapSection
          rule={rule} isStd={isStd}
          entityId={entityId} sourcePrefix={sourcePrefix} column={column}
          onTokenBlockSaved={onTokenBlockSaved}
        />

        {rule.merit && (
          <CollapsibleSection title="Rule Merit" icon={faCheckCircle} defaultOpen={false}>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {[
                      { label: 'Overall Merit',    value: rule.merit.overall_merit },
                      { label: 'Confidence Score', value: rule.merit.confidence_score },
                    ].map(({ label, value }, i) => (
                      <div key={i} style={{
                        background: T.bgSidebar, border: `1px solid ${T.border}`,
                        borderRadius: 10, padding: '10px 14px',
                      }}>
                        <div style={{
                          fontFamily: "'JetBrains Mono', monospace",
                          fontSize: 9, fontWeight: 700, textTransform: 'uppercase' as const,
                          letterSpacing: '.08em', color: T.textMuted, marginBottom: 4,
                        }}>
                          {label}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: T.brand }}>{value}</div>
                      </div>
                    ))}
                  </div>
                  {rule.merit.summary && (
                    <div style={{ marginTop: 10, fontSize: 13, color: T.textSecond, fontStyle: 'italic' }}>
                      {rule.merit.summary}
                    </div>
                  )}
                
          </CollapsibleSection>
        )}

      </div>



      {/* ── Footer ── */}
      <div style={{
        padding: '14px 24px', background: T.bgSidebar,
        borderTop: `1px solid ${T.border}`,
        borderRadius: '0 0 12px 12px',
        display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' as const,
      }}>
        {onDQRuleTuningTrigger && (
          <button onClick={onDQRuleTuningTrigger} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontSize: 13, fontWeight: 700,
            color: T.purple, background: T.purpleBg,
            border: `1.5px solid ${T.purpleBorder}`,
            borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
          }}>
            <FontAwesomeIcon icon={faWandMagicSparkles} style={{ fontSize: 12 }} /> AI Tune
          </button>
        )}
        {onDQRuleUpdateLifeCycle && (
          <>
            <button onClick={() => onDQRuleUpdateLifeCycle(rule, 'Rejected')} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 13, fontWeight: 700,
              color: T.red, background: T.redBg,
              border: `1.5px solid ${T.redBorder}`,
              borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
            }}>
              <FontAwesomeIcon icon={faTimesCircle} style={{ fontSize: 12 }} /> Reject
            </button>
            <button onClick={() => onDQRuleUpdateLifeCycle(rule, 'Approved')} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              fontSize: 13, fontWeight: 700,
              color: '#fff', background: T.green,
              border: `1.5px solid ${T.green}`,
              borderRadius: 8, padding: '8px 16px', cursor: 'pointer',
            }}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ fontSize: 12 }} /> Approve
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default RuleDetailsModal;